import { Modal } from "antd";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpin from "react-loading-spin";

import { IQuestionDisplayProps } from "../../../../../../../@types/Surveys";
import { Close } from "../../../../../../../assets/icons/Close";
import { ProgressBar } from "../../../../../../../components/ProgressBar";
import { useDynamicsurveys } from "../../../../../../../hooks/useDynamicSurveys";
import { surveysService } from "../../../../../../../services/surveysService";
import {
  getFileNameFromPath,
  isVideoPath,
  parsePaths,
  serializePaths,
} from "../../../../../../../utils/surveyMediaAnswer";
import { IPendingUpload } from "../../../../../Answer Survey/Components/Survey/Components/AddMediaButtons/AddMediaButtons";
import {
  Description,
  FormAnswerField,
  Title,
  ImageAttachedContainer,
  ImageAttachedText,
  DeleteButton,
  ErrorContainer,
  ErrorText,
  WarningText,
  PreviewModalContent,
} from "./MediaQuestionDisplay.styles";

interface IMediaQuestionDisplayProps extends IQuestionDisplayProps {
  error?: string;
  clearError?: () => void;
  pendingUploads?: IPendingUpload[];
  mediaBlobUrls?: Record<string, string>;
  forgetMediaBlobUrl?: (path: string) => void;
  blurryPaths?: Record<string, true>;
  forgetBlurryPath?: (path: string) => void;
}

const BLUR_WARNING_KEY =
  "survey_fixed_translations_media_question_warning_blurry";
const BLUR_WARNING_FALLBACK = "The attached image might be blurry";

export function MediaQuestionDisplay({
  title,
  description,
  questionId,
  isForms,
  error,
  clearError,
  pendingUploads,
  mediaBlobUrls,
  forgetMediaBlobUrl,
  blurryPaths,
  forgetBlurryPath,
}: IMediaQuestionDisplayProps) {
  const { surveyAnswerArray, updateSurveyAnswerArray } = useDynamicsurveys();
  const [deletingPath, setDeletingPath] = useState<string | undefined>();
  const [previewPath, setPreviewPath] = useState<string | undefined>();

  const { t } = useTranslation();

  const storedAnswer = surveyAnswerArray.find(
    (surveyAnswer) => surveyAnswer.surveyQuestionId === questionId
  )?.answerValue;

  const paths = parsePaths(storedAnswer);

  const translatedOrFallback = (key: string, fallback: string) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const blurWarningText = translatedOrFallback(
    BLUR_WARNING_KEY,
    BLUR_WARNING_FALLBACK
  );

  const handleDeletePath = async (path: string) => {
    if (!questionId) return;
    setDeletingPath(path);

    try {
      const response = await surveysService.deleteTempSurveyAnswerMedia(path);

      if (response.status === 204 || response.status === 200) {
        const remaining = paths.filter((p) => p !== path);
        updateSurveyAnswerArray({
          surveyQuestionId: questionId,
          answerValue: remaining.length > 0 ? serializePaths(remaining) : "",
        });
        forgetMediaBlobUrl?.(path);
        forgetBlurryPath?.(path);
        clearError?.();
        if (previewPath === path) setPreviewPath(undefined);
      }
    } finally {
      setDeletingPath(undefined);
    }
  };

  const successLabel = `${t(
    "survey_fixed_translations_media_question_success"
  )}`;

  const previewUrl = previewPath ? mediaBlobUrls?.[previewPath] : undefined;

  return (
    <FormAnswerField isForms={isForms}>
      <Title>{title}</Title>
      <Description>{description}</Description>

      {error && (
        <ErrorContainer>
          <ErrorText>{error}</ErrorText>
        </ErrorContainer>
      )}

      {paths.map((path) => {
        const hasPreview = Boolean(mediaBlobUrls?.[path]);
        const isBlurry = Boolean(blurryPaths?.[path]);
        return (
          <Fragment key={path}>
            <ImageAttachedContainer
              clickable={hasPreview}
              onClick={hasPreview ? () => setPreviewPath(path) : undefined}
            >
              <ImageAttachedText>
                {getFileNameFromPath(path) || successLabel}
              </ImageAttachedText>
              <DeleteButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePath(path);
                }}
                disabled={deletingPath === path}
              >
                {deletingPath === path ? (
                  <LoadingSpin
                    size="12px"
                    primaryColor="#FFFFFF"
                    secondaryColor="rgba(255,255,255,0.3)"
                    width="2px"
                  />
                ) : (
                  <Close color="#FFFFFF" />
                )}
              </DeleteButton>
            </ImageAttachedContainer>
            {isBlurry && <WarningText>{`* ${blurWarningText}`}</WarningText>}
          </Fragment>
        );
      })}

      {pendingUploads?.map((upload) => (
        <ImageAttachedContainer key={upload.id}>
          <ImageAttachedText>{upload.name}</ImageAttachedText>
          <ProgressBar percent={upload.percent} maxWidth={120} />
        </ImageAttachedContainer>
      ))}

      <Modal
        open={previewPath !== undefined}
        zIndex={3001}
        width="auto"
        centered
        footer={null}
        onCancel={() => setPreviewPath(undefined)}
        destroyOnHidden
      >
        <PreviewModalContent>
          {previewUrl &&
            previewPath &&
            (isVideoPath(previewPath) ? (
              // Patient-uploaded clinical clip; no caption track is generated
              // upstream so jsx-a11y/media-has-caption isn't actionable here.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                src={previewUrl}
                controls
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            ) : (
              <img
                src={previewUrl}
                alt=""
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            ))}
        </PreviewModalContent>
      </Modal>
    </FormAnswerField>
  );
}
